// Project-level routing (RF-CFG-06/12/14, RF-MAESTRO-01/08): the effective table with
// origin and "restore inheritance", a project override draft (roles, Gauntlet mode, hard
// budget) previewed through the server validation before activation, the project versions
// and the Maestro override (a switch opens a new supervision epoch).
import { useState } from "react";
import type { AgentRole, Role } from "@maestros/contracts";
import { useI18n } from "../../i18n/index.tsx";
import { activateRoutingConfig, createRoutingDraft, getEffectiveConfig, getEffectivePreview, getMaestro, listAgentProfiles, listModelTargets, listRoutingConfigs, putMaestro, validateRoutingConfig } from "../../api/endpoints-wave2.ts";
import type { AgentProfile, EffectiveConfigView, ModelTarget, RoutingConfigBody, RoutingConfigLayer, RoutingConfigRecord } from "../../api/types-wave2.ts";
import { useSession } from "../../session/SessionProvider.tsx";
import { useAsync, useDebouncedCallback } from "../../lib/useAsync.ts";
import { useRealtimeEvents } from "../../realtime/RealtimeProvider.tsx";
import { Button } from "../../components/Button.tsx";
import { SelectField, TextField } from "../../components/Field.tsx";
import { ErrorBanner, LoadingBlock } from "../../components/Feedback.tsx";
import { Pill } from "../../components/Status.tsx";
import { ActionError } from "../../components/ActionError.tsx";
import { useToast } from "../../components/Toast.tsx";
import { GitBranchIcon, ShieldIcon } from "../../components/icons.tsx";
import { OriginBadge, ROLE_IDS } from "../../features/config/shared.tsx";
import { EffectiveTable, RoutingVersionList, ValidationSummary } from "../../features/config/routing.tsx";

const RANK: Record<Role, number> = { observer: 0, reviewer: 1, operator: 2, maintainer: 3, admin: 4 };

function useCanMaintain(projectId: string): boolean {
  const { me } = useSession();
  if (!me) return false;
  if (RANK[me.role] >= RANK.maintainer) return true;
  const m = (me.project_memberships ?? []).find((x) => x.project_id === projectId || x.project_id === "*");
  return m ? RANK[m.role] >= RANK.maintainer : false;
}

function targetName(targets: ModelTarget[], id: string | null | undefined): string {
  if (!id) return "—";
  const x = targets.find((m) => m.id === id);
  return x ? `${x.display_name} (${x.external_model_id})` : id;
}

type Override = { roles: Partial<Record<AgentRole, string>>; gauntletMode: "" | "conventional" | "gauntlet"; hardLimit: string };

function overrideOf(body: RoutingConfigLayer | undefined): Override {
  const roles: Partial<Record<AgentRole, string>> = {};
  for (const [role, route] of Object.entries(body?.roles ?? {})) if (route?.default_agent_profile_id) roles[role as AgentRole] = route.default_agent_profile_id;
  return { roles, gauntletMode: body?.gauntlet?.mode ?? "", hardLimit: body?.budgets?.hard_limit_cents?.toString() ?? "" };
}

/** Builds the project layer: only overridden fields are present (the rest inherits). */
function bodyOf(base: RoutingConfigLayer | undefined, o: Override, profiles: AgentProfile[]): RoutingConfigLayer {
  const out: RoutingConfigLayer = { ...(base ?? {}) };
  const roles: RoutingConfigBody["roles"] = {};
  for (const [role, id] of Object.entries(o.roles) as [AgentRole, string][]) {
    if (!id) continue;
    const previous = base?.roles?.[role];
    roles[role] = {
      agent_profile_ids: Array.from(new Set([id, ...(previous?.agent_profile_ids ?? []).filter((x) => profiles.some((p) => p.id === x && p.role === role))])),
      default_agent_profile_id: id,
      required_capabilities: previous?.required_capabilities ?? [],
      fallbacks: previous?.fallbacks ?? [],
    };
  }
  if (Object.keys(roles).length > 0) out.roles = roles;
  else delete out.roles;
  if (o.gauntletMode) out.gauntlet = { mode: o.gauntletMode, required_for: base?.gauntlet?.required_for ?? [], max_rounds: base?.gauntlet?.max_rounds ?? 4, stagnation_rounds: base?.gauntlet?.stagnation_rounds ?? 2 };
  else delete out.gauntlet;
  const hard = o.hardLimit.trim() === "" ? null : Number(o.hardLimit);
  if (hard !== null && Number.isFinite(hard)) out.budgets = { soft_limit_cents: base?.budgets?.soft_limit_cents ?? null, hard_limit_cents: hard, window: base?.budgets?.window ?? "month" };
  else delete out.budgets;
  return out;
}

export function ProjectRoutingPanel({ projectId }: { projectId: string }) {
  const { t } = useI18n();
  const { toast } = useToast();
  const canWrite = useCanMaintain(projectId);
  const effective = useAsync((s) => getEffectiveConfig(projectId, s), [projectId]);
  const configs = useAsync((s) => listRoutingConfigs(projectId, s).catch(() => ({ items: [] as RoutingConfigRecord[] })), [projectId]);
  const profiles = useAsync((s) => listAgentProfiles(projectId, s).catch(() => ({ items: [] as AgentProfile[] })), [projectId]);
  const targets = useAsync((s) => listModelTargets(s).catch(() => ({ items: [] as ModelTarget[] })), []);
  const reloadAll = () => {
    effective.reload();
    configs.reload();
  };
  const refresh = useDebouncedCallback(reloadAll, 400);
  useRealtimeEvents((e) => {
    if (e.type === "routing.config.activated" || e.aggregate_type === "routing_config" || e.aggregate_type === "agent_profile") refresh();
  }, projectId);

  const list = configs.data?.items ?? [];
  const active = list.find((c) => c.state === "active") ?? null;
  const [override, setOverride] = useState<Override | null>(null);
  const [draft, setDraft] = useState<RoutingConfigRecord | null>(null);
  const [busy, setBusy] = useState<"save" | "activate" | null>(null);
  const [error, setError] = useState<unknown>(null);

  const startEdit = () => {
    setOverride(overrideOf(active?.body));
    setDraft(null);
    setError(null);
  };

  const saveDraft = async (o: Override) => {
    setBusy("save");
    setError(null);
    try {
      const created = await createRoutingDraft(projectId, bodyOf(active?.body, o, profiles.data?.items ?? []));
      // The server validation of the draft is the preview of what activation would resolve.
      setDraft(await validateRoutingConfig(created.id));
      configs.reload();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  };

  const restoreInheritance = (role: AgentRole) => {
    const o = overrideOf(active?.body);
    delete o.roles[role];
    setOverride(o);
    void saveDraft(o);
  };

  const activate = async () => {
    if (!draft) return;
    setBusy("activate");
    setError(null);
    try {
      await activateRoutingConfig(draft.id, active?.version ?? 0);
      toast(t("routing.activated", { n: draft.version }), "success");
      setDraft(null);
      setOverride(null);
      reloadAll();
    } catch (e) {
      setError(e);
      configs.reload();
    } finally {
      setBusy(null);
    }
  };

  const profilesList = profiles.data?.items ?? [];
  const tlist = targets.data?.items ?? [];

  return (
    <section className="card" aria-labelledby="project-routing-title" data-testid="project-routing">
      <div className="card-header">
        <GitBranchIcon size={18} />
        <h2 id="project-routing-title" className="card-title" style={{ flex: 1 }}>
          {t("projectRouting.title")}
        </h2>
        {canWrite && !override ? (
          <Button size="sm" onClick={startEdit}>
            {t("projectRouting.editOverride")}
          </Button>
        ) : null}
      </div>
      <div className="card-body stack">
        <p className="muted">{t("projectRouting.intro")}</p>
        {effective.loading ? (
          <LoadingBlock label={t("common.loading")} />
        ) : effective.error ? (
          <ErrorBanner error={effective.error} onRetry={effective.reload} />
        ) : effective.data ? (
          <EffectiveTable
            view={effective.data}
            targets={tlist}
            action={
              canWrite
                ? (row) =>
                    row.origin === "project" ? (
                      <Button size="sm" variant="ghost" onClick={() => restoreInheritance(row.role)} disabled={busy !== null}>
                        {t("projectRouting.restoreInheritance")}
                      </Button>
                    ) : null
                : undefined
            }
          />
        ) : null}

        <ActionError error={error} onDismiss={() => setError(null)} />

        {override ? (
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              void saveDraft(override);
            }}
            data-testid="override-form"
            aria-label={t("projectRouting.overrideTitle")}
          >
            <h3 className="subheading">{t("projectRouting.overrideTitle")}</h3>
            <p className="field-hint">{t("projectRouting.overrideHint")}</p>
            <div className="form-grid">
              {ROLE_IDS.filter((r) => r !== "maestro").map((role) => {
                const options = profilesList.filter((p) => p.role === role && p.state !== "disabled");
                return (
                  <SelectField
                    key={role}
                    label={t(`agentRole.${role}`)}
                    value={override.roles[role] ?? ""}
                    onChange={(e) => setOverride((o) => (o ? { ...o, roles: { ...o.roles, [role]: e.target.value } } : o))}
                  >
                    <option value="">{t("projectRouting.inherit")}</option>
                    {options.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                        {p.project_id ? ` (${t("config.projectScope")})` : ""}
                      </option>
                    ))}
                  </SelectField>
                );
              })}
              <SelectField label={t("routing.gauntletMode")} value={override.gauntletMode} onChange={(e) => setOverride((o) => (o ? { ...o, gauntletMode: e.target.value as Override["gauntletMode"] } : o))}>
                <option value="">{t("projectRouting.inherit")}</option>
                <option value="conventional">{t("gauntletMode.conventional")}</option>
                <option value="gauntlet">{t("gauntletMode.gauntlet")}</option>
              </SelectField>
              <TextField
                label={t("routing.hardLimit")}
                hint={t("projectRouting.hardLimitHint")}
                type="number"
                min={0}
                value={override.hardLimit}
                onChange={(e) => setOverride((o) => (o ? { ...o, hardLimit: e.target.value } : o))}
              />
            </div>
            <div className="row row--wrap">
              <Button type="submit" variant="primary" loading={busy === "save"}>
                {t("projectRouting.preview")}
              </Button>
              <Button
                onClick={() => {
                  setOverride(null);
                  setDraft(null);
                }}
                disabled={busy !== null}
              >
                {t("common.cancel")}
              </Button>
            </div>
          </form>
        ) : null}

        {draft ? <DraftPreview draft={draft} effective={effective.data ?? null} targets={tlist} busy={busy === "activate"} canWrite={canWrite} onActivate={() => void activate()} /> : null}

        <details className="disclosure">
          <summary>{t("projectRouting.versions", { n: list.length })}</summary>
          <RoutingVersionList configs={list} canWrite={canWrite} onChanged={reloadAll} showDiff emptyText={t("projectRouting.noVersions")} />
        </details>

        <MaestroOverride projectId={projectId} canWrite={canWrite} targets={tlist} onChanged={reloadAll} />
      </div>
    </section>
  );
}

/** Preview before activation: server validation of the draft + the rows it changes. */
function DraftPreview({
  draft,
  effective,
  targets,
  busy,
  canWrite,
  onActivate,
}: {
  draft: RoutingConfigRecord;
  effective: EffectiveConfigView | null;
  targets: ModelTarget[];
  busy: boolean;
  canWrite: boolean;
  onActivate: () => void;
}) {
  const { t } = useI18n();
  // Server-resolved table as if this draft were active (read-only; nothing is activated).
  const preview = useAsync((signal) => getEffectivePreview(draft.id, null, signal), [draft.id]);
  const rowText = (r: EffectiveConfigView["rows"][number] | undefined) => (r ? `${r.agent_name ?? "—"} · ${r.model_display ?? "—"}` : "—");
  const changes = preview.data
    ? preview.data.rows
        .map((next) => {
          const current = effective?.rows.find((r) => r.role === next.role);
          if (current && rowText(current) === rowText(next) && current.origin === next.origin) return null;
          return { role: next.role, current: rowText(current), currentOrigin: current?.origin ?? "unset", proposed: rowText(next), proposedOrigin: next.origin };
        })
        .filter((x): x is NonNullable<typeof x> => x !== null)
    : [];
  const ok = draft.validation?.ok === true;
  return (
    <div className="card card--pad stack draft-preview" data-testid="draft-preview">
      <div className="row row--wrap">
        <strong>{t("projectRouting.previewTitle", { n: draft.version })}</strong>
        <ValidationSummary config={draft} />
      </div>
      <p className="field-hint">{t("projectRouting.previewNote")}</p>
      {preview.loading ? (
        <LoadingBlock label={t("common.loading")} lines={2} />
      ) : preview.error ? (
        <ErrorBanner error={preview.error} onRetry={preview.reload} title={t("projectRouting.previewFailed")} />
      ) : null}
      {changes.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{t("projectRouting.changes")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("agents.role")}</th>
                <th scope="col">{t("projectRouting.current")}</th>
                <th scope="col">{t("projectRouting.proposed")}</th>
              </tr>
            </thead>
            <tbody>
              {changes.map((c) => (
                <tr key={c.role} data-testid={`preview-row-${c.role}`}>
                  <th scope="row">{t(`agentRole.${c.role}`)}</th>
                  <td>
                    {c.current} <OriginBadge origin={c.currentOrigin} />
                  </td>
                  <td>
                    {c.proposed} <OriginBadge origin={c.proposedOrigin} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : preview.data ? (
        <p className="subtle">{t("projectRouting.noRoleChanges")}</p>
      ) : null}
      {preview.data ? (
        <details className="disclosure" data-testid="preview-table">
          <summary>{t("projectRouting.fullPreview")}</summary>
          <EffectiveTable view={preview.data} targets={targets} />
        </details>
      ) : null}
      {canWrite ? (
        <div className="row row--wrap">
          <Button variant="primary" onClick={onActivate} loading={busy} disabled={!ok} aria-describedby={!ok ? "draft-not-valid" : undefined}>
            {t("projectRouting.activate")}
          </Button>
          {!ok ? (
            <span id="draft-not-valid" className="field-hint">
              {t("projectRouting.fixImpediments")}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function MaestroOverride({ projectId, canWrite, targets, onChanged }: { projectId: string; canWrite: boolean; targets: ModelTarget[]; onChanged: () => void }) {
  const { t, formatDateTime } = useI18n();
  const { toast } = useToast();
  const maestro = useAsync((s) => getMaestro(projectId, s), [projectId]);
  const [editing, setEditing] = useState(false);
  const [primary, setPrimary] = useState("");
  const [subs, setSubs] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [epochs, setEpochs] = useState<{ id: string; maestro_epoch: number }[] | null>(null);
  const strong = targets.filter((x) => x.strong);
  const m = maestro.data;

  const submit = async () => {
    if (!m || !primary) return;
    setBusy(true);
    setError(null);
    try {
      const result = await putMaestro(projectId, { model_target_id: primary, substitute_model_target_ids: subs, expected_version: m.routing_config_versions.project ?? 0 });
      setEpochs(result.goal_runs);
      toast(t("maestro.saved"), "success");
      setEditing(false);
      maestro.reload();
      onChanged();
    } catch (e) {
      setError(e);
      maestro.reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack maestro-override" data-testid="maestro-override">
      <h3 className="subheading row">
        <ShieldIcon size={16} /> {t("maestro.title")}
      </h3>
      {maestro.loading ? (
        <LoadingBlock label={t("common.loading")} lines={2} />
      ) : maestro.error ? (
        <ErrorBanner error={maestro.error} onRetry={maestro.reload} />
      ) : m ? (
        <>
          <dl className="kv">
            <div>
              <dt>{t("maestro.primary")}</dt>
              <dd>
                {m.maestro ? (
                  <>
                    {m.maestro.display_name} <span className="mono subtle">{m.maestro.external_model_id}</span> {m.maestro.strong ? <span className="badge badge--accent">{t("models.strong")}</span> : <span className="badge badge--warning">{t("routing.notStrong")}</span>}
                  </>
                ) : (
                  <span className="subtle">{t("routing.maestroNone")}</span>
                )}
              </dd>
            </div>
            <div>
              <dt>{t("routing.substitutes")}</dt>
              <dd>{m.substitutes.filter((s) => s !== null).length > 0 ? m.substitutes.map((s) => s?.display_name ?? "?").join(", ") : "—"}</dd>
            </div>
            <div>
              <dt>{t("effective.origin")}</dt>
              <dd>
                <OriginBadge origin={m.origin} />
              </dd>
            </div>
            <div>
              <dt>{t("maestro.epoch")}</dt>
              <dd data-testid="maestro-epoch">{m.epoch ?? t("maestro.noActiveGoal")}</dd>
            </div>
            <div>
              <dt>{t("maestro.readiness")}</dt>
              <dd>
                {m.ready ? (
                  <Pill tone="success" icon="check">
                    {t("effective.ready")}
                  </Pill>
                ) : (
                  <span className="tone-warning user-content">{m.impediment ?? t("effective.notReady")}</span>
                )}
              </dd>
            </div>
            <div>
              <dt>{t("maestro.lastReview")}</dt>
              <dd>{m.last_review ? `${String(m.last_review["verdict"] ?? m.last_review["state"] ?? "")} · ${formatDateTime(String(m.last_review["created_at"] ?? ""))}` : "—"}</dd>
            </div>
          </dl>
          {epochs && epochs.length > 0 ? (
            <p className="tone-info" role="status" data-testid="maestro-epochs">
              {t("maestro.newEpoch", { list: epochs.map((e) => e.maestro_epoch).join(", ") })}
            </p>
          ) : null}
          {canWrite && !editing ? (
            <div>
              <Button
                size="sm"
                onClick={() => {
                  setPrimary(m.maestro?.model_target_id ?? "");
                  setSubs(m.substitutes.filter((s): s is NonNullable<typeof s> => s !== null).map((s) => s.model_target_id));
                  setError(null);
                  setEditing(true);
                }}
              >
                {t("maestro.override")}
              </Button>
            </div>
          ) : null}
          {editing ? (
            <form
              className="stack"
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
              aria-label={t("maestro.override")}
            >
              <p className="field-hint">{t("maestro.overrideHint")}</p>
              <SelectField label={t("maestro.primary")} value={primary} onChange={(e) => setPrimary(e.target.value)} hint={strong.length === 0 ? t("routing.noStrongTargets") : undefined}>
                <option value="">{t("agents.chooseModel")}</option>
                {targets.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.display_name} ({x.external_model_id}){x.strong ? ` — ${t("models.strong")}` : ` — ${t("routing.notStrong")}`}
                  </option>
                ))}
              </SelectField>
              <fieldset className="field">
                <legend className="field-label">{t("routing.substitutes")}</legend>
                {strong
                  .filter((x) => x.id !== primary)
                  .map((x) => (
                    <label key={x.id} className="choice">
                      <input type="checkbox" checked={subs.includes(x.id)} onChange={(e) => setSubs((s) => (e.target.checked ? [...s, x.id].slice(0, 5) : s.filter((v) => v !== x.id)))} />
                      <span>{x.display_name}</span>
                    </label>
                  ))}
              </fieldset>
              <ActionError error={error} />
              <div className="row row--wrap">
                <Button type="submit" variant="primary" loading={busy} disabled={!primary}>
                  {t("maestro.apply")}
                </Button>
                <Button onClick={() => setEditing(false)} disabled={busy}>
                  {t("common.cancel")}
                </Button>
              </div>
            </form>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
